import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Input, RadioCircle, RadioInput, Textarea } from '@/components/Forms';
import Text from '@/components/Text';

import { ImageVariationParams } from '.';
import SimpleUploadedImageRow from '../SimpleUploadedImageRow';
import UploadSingleImage from '../UploadSingleImage';
import { ImageData, LabelText } from '../VariationOne';
import { ActionBox, StyledRadiosLabel } from '../shared/styledComponents';
import { GridInner, InputsGrid, NameTextArea } from '../VariationThree';
import ToggleButton from './ToggleButton';

const DIVIDER_COLORS = ['black', 'blue'];

const COMMON_COLORS = ['#55C9EE', '#36465D', '#FFAF56', '#3B5998'];

const CHANNEL_IMAGE_OPTIONS = ['reddit', 'none', 'custom'];

const TextAreaHolder = styled.div`
  padding: 24px;
`;

const StyledTextarea = styled(Textarea)`
  height: 132px;
`;

const ColorInputHolder = styled.div`
  flex: 1;
`;

const ColorInput = styled(Input)`
  padding: 4px 8px;
  height: 46px;
  width: 100%;
`;

const ChannelNameHolder = styled.div`
  margin-top: 16px;
`;

const ColorHolder = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
  width: 100%;
`;

export const ColorResetButton = styled.button`
  display: flex;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  width: 46px;
  height: 46px;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.gray};
  transition: all ease-out 0.2s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.black};
  }
  &:active {
    background-color: ${({ theme }) => theme.colors.grayscale};
  }
`;

const ColorColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const MostUsedColorHolder = styled.div`
  flex-shrink: 0;
`;

const ColorsButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ColorButton = styled.button<{ $color: string }>`
  height: 46px;
  width: 40px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  background: ${({ $color }) => $color};
  transition: border-color ease-out 0.2s;
  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.grayscaleDark};
  }
  &:active {
    border: 1px solid ${({ theme }) => theme.colors.gray};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 24px;
`;

const ButtonsHolder = styled.div`
  display: flex;
  gap: 32px;
`;

const RemoveButton = styled(Button)`
  height: 38px;
  font-size: 14px;
  padding: 0 32px;
`;

const ChannelImageHolder = styled.div`
  margin-top: 16px;
`;

const UploadAuthorImages = styled(UploadSingleImage)`
  height: 98px;
`;

interface Props {
  cover: ImageVariationParams;
  coverIndex: number;
  setCoverParameters: React.Dispatch<
    React.SetStateAction<ImageVariationParams[]>
  >;
}

const CoverBox: FC<Props> = ({ cover, coverIndex, setCoverParameters }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [images, setImages] = useState<ImageData[]>([]);

  const setAuthorImagesAtIndex = (images: ImageData[]) => {
    setCoverParameters((prevParams) => {
      const updatedParams = [...prevParams];

      updatedParams[coverIndex].authorImages = images;
      return updatedParams;
    });
  };

  const setColor = (color: string) => {
    setCoverParameters((prevParams) => {
      const updatedParams = [...prevParams];
      updatedParams[coverIndex].coverColor = color;
      return updatedParams;
    });
  };

  const setNameColor = (color: string) => {
    setCoverParameters((prevParams) => {
      const updatedParams = [...prevParams];
      updatedParams[coverIndex].channelNameColor = color;
      return updatedParams;
    });
  };

  const setAuthorName = (name: string) => {
    setCoverParameters((prevParams) => {
      const updatedParams = [...prevParams];
      updatedParams[coverIndex].authorName = name;
      return updatedParams;
    });
  };

  const setChannelImageOption = (option: string) => {
    setCoverParameters((prevParams) => {
      const updatedParams = [...prevParams];
      updatedParams[coverIndex].channelImageOption = option;
      return updatedParams;
    });
  };

  const setText = (text: string) => {
    setCoverParameters((prevParams) => {
      const updatedParams = [...prevParams];
      updatedParams[coverIndex].coverText = text;
      return updatedParams;
    });
  };

  const setFontSize = (fontSize: string) => {
    setCoverParameters((prevParams) => {
      const updatedParams = [...prevParams];
      updatedParams[coverIndex].fontSize = fontSize;
      return updatedParams;
    });
  };

  const renderAuthorImages = () => {
    return cover.authorImages.map((image, index) => (
      <SimpleUploadedImageRow
        key={index}
        imageIndex={index}
        image={image}
        setImages={setImages}
      />
    ));
  };

  const onRemoveVariation = () => {
    setCoverParameters((prev) => prev.filter((_, idx) => idx !== coverIndex));
  };

  useEffect(() => {
    setAuthorImagesAtIndex(images);
    // eslint-disable-next-line
  }, [images, setImages, coverIndex]);

  return (
    <>
      <Header>
        <Text $type="button">Variation #{coverIndex + 1}</Text>
        <ButtonsHolder>
          {coverIndex !== 0 && (
            <RemoveButton color="red" onClick={onRemoveVariation}>
              Remove variation #{coverIndex + 1}
            </RemoveButton>
          )}
          <ToggleButton
            onToggle={() => setIsOpen((prev) => !prev)}
            isOpen={isOpen}
          />
        </ButtonsHolder>
      </Header>
      {isOpen && (
        <>
          <TextAreaHolder>
            <LabelText $type="smallHeadline">Cover Text</LabelText>
            <StyledTextarea
              placeholder="Add Text"
              onChange={(e) => setText(e.target.value)}
              value={cover.coverText}
            ></StyledTextarea>
          </TextAreaHolder>
          <InputsGrid>
            <GridInner>
              <LabelText $type="smallHeadline">Channel Image</LabelText>
              <ActionBox>
                {CHANNEL_IMAGE_OPTIONS.map((option) => (
                  <StyledRadiosLabel key={option}>
                    {option}
                    <RadioInput
                      type="radio"
                      name={`${coverIndex}-channel_image`}
                      checked={cover.channelImageOption === option}
                      onChange={() => setChannelImageOption(option)}
                    />
                    <RadioCircle />
                  </StyledRadiosLabel>
                ))}
              </ActionBox>
              {cover.channelImageOption === 'custom' && (
                <ChannelImageHolder>
                  <LabelText $type="smallHeadline">Channel Image</LabelText>
                  {!!cover.authorImages.length ? (
                    <>{renderAuthorImages()}</>
                  ) : (
                    <>
                      <UploadAuthorImages
                        coverIndex={coverIndex}
                        setImages={setImages}
                      />
                    </>
                  )}
                </ChannelImageHolder>
              )}
            </GridInner>
            <GridInner>
              <LabelText $type="smallHeadline">Channel Name Color</LabelText>
              <ActionBox>
                {DIVIDER_COLORS.map((color) => (
                  <StyledRadiosLabel key={color}>
                    {color}
                    <RadioInput
                      type="radio"
                      name={`${coverIndex}-name_color`}
                      checked={cover.channelNameColor === color}
                      onChange={() => setNameColor(color)}
                    />
                    <RadioCircle />
                  </StyledRadiosLabel>
                ))}
              </ActionBox>
              <ChannelNameHolder>
                <LabelText $type="smallHeadline">Channel Name</LabelText>
                <NameTextArea
                  placeholder="Add Name"
                  onChange={(e) => setAuthorName(e.target.value)}
                  value={cover.authorName}
                ></NameTextArea>
              </ChannelNameHolder>
            </GridInner>
            <GridInner>
              <ColorColumn>
                <ColorHolder>
                  <ColorInputHolder>
                    <LabelText $type="smallHeadline">
                      Cover Frame Color
                    </LabelText>
                    <ColorInput
                      onChange={(e) => setColor(e.target.value)}
                      value={cover.coverColor}
                      type="color"
                    />
                  </ColorInputHolder>
                  <ColorResetButton onClick={() => setColor('#FFB054')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M7.48438 9.34766H2.98438V4.84766"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.16635 17.8336C7.32014 18.9874 8.79015 19.7732 10.3905 20.0915C11.9908 20.4098 13.6496 20.2464 15.1571 19.622C16.6646 18.9976 17.9531 17.9402 18.8596 16.5835C19.7661 15.2268 20.25 13.6317 20.25 12C20.25 10.3683 19.7661 8.77325 18.8596 7.41655C17.9531 6.05984 16.6646 5.00242 15.1571 4.378C13.6496 3.75357 11.9908 3.5902 10.3905 3.90853C8.79015 4.22685 7.32014 5.01259 6.16635 6.16637L2.98438 9.34835"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ColorResetButton>
                </ColorHolder>
                <MostUsedColorHolder>
                  <LabelText $type="smallHeadline">Most Used Colors</LabelText>
                  <ColorsButtons>
                    {COMMON_COLORS.map((color, index) => (
                      <ColorButton
                        key={index}
                        $color={color}
                        onClick={() => setColor(color)}
                      />
                    ))}
                  </ColorsButtons>
                </MostUsedColorHolder>
              </ColorColumn>
            </GridInner>
            <GridInner>
              <LabelText $type="smallHeadline">Font Size</LabelText>
              <Input
                value={cover.fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                placeholder="Font Size (px)"
                type="number"
              />
            </GridInner>
          </InputsGrid>
        </>
      )}
    </>
  );
};

export default CoverBox;
